import deepMerge from 'lodash/merge';
import type { Config } from 'tailwindcss';
import { ApexOptions } from 'apexcharts';
import { numberFromCSSUnit, shortenNumber } from '@/utils/number.utils';
import { buildDonutChartTooltip, donutChartLegendFormater } from '@/components/chart.utils';

export const fontMobile = {
  cssClass: 'text-xs font-normal',
  fontFamily: 'Public, sans-serif',
};

export const fontTabletDesktop = {
  cssClass: 'text-sm font-normal',
  fontFamily: 'Public, sans-serif',
};

const FONT_SIZE = {
  xxs: '10px',
  xs: '12px',
  sm: '14px',
  base: '16px',
};

export type ChartOptions = ApexOptions;
export const computeChartOptions = (options: object, theme: Config, chartType = ''): ChartOptions => {
  const PLOT_OPTIONS: { [key: string]: ChartOptions } = {
    donut: {
      legend: {
        formatter: donutChartLegendFormater,
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'middle',
        dropShadow: {
          enabled: false,
        },
        style: {
          fontSize: FONT_SIZE.sm,
          fontWeight: theme.fontWeight['normal'],
          colors: [theme.colors['text']],
        },
        formatter: (val: number): string => {
          return `${val.toFixed(0)}%`;
        },
      },
      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        custom: buildDonutChartTooltip,
      },
      plotOptions: {
        pie: {
          offsetY: 10,
          dataLabels: {
            minAngleToShowLabel: 20,
          },
          expandOnClick: true,
          donut: {
            labels: {
              show: false,
              name: {
                fontSize: FONT_SIZE.sm,
                color: theme.colors['text'],
                offsetY: -4,
              },
              value: {
                color: theme.colors['text-support'],
                fontSize: FONT_SIZE.base,
                offsetY: 4,
                formatter: (val: string): string => {
                  return `${shortenNumber(parseFloat(val), 1)}`;
                },
              },
            },
          },
        },
      },
    },
  };
  const PLOT_OPTIONS_RESPONSIVE: { [key: string]: ChartOptions } = {
    donut: {
      tooltip: {
        enabled: true,
        custom: buildDonutChartTooltip,
      },
    },
  };

  const COMMON_CHART_OPTIONS: ChartOptions = {
    chart: {
      parentHeightOffset: 10,
      width: '100%',
      fontFamily: 'Inter, sans-serif',
      dropShadow: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      x: {
        show: false,
      },
    },
    title: {
      align: 'left',
      offsetX: -10,
      style: {
        fontSize: FONT_SIZE.sm,
        fontWeight: theme.fontWeight['normal'],
        color: theme.colors['text-support'],
      },
    },
    dataLabels: {
      style: {
        fontSize: FONT_SIZE.sm,
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: FONT_SIZE.sm,
      fontWeight: 400,
      fontFamily: 'Public, sans-serif',
      itemMargin: {
        horizontal: 8,
      },
      labels: {
        colors: theme.colors['text-support'],
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.colors['text-support'],
          fontSize: FONT_SIZE.sm,
          fontWeight: 400,
          fontFamily: 'Public, sans-serif',
          cssClass: 'text-xs sm:text-sm  font-normal',
        },
        offsetX: -10,
        offsetY: 4,
        align: 'center',
        maxWidth: 300,
      },
    },
    xaxis: {
      labels: {
        style: {
          colors: theme.colors['text-support'],
          fontSize: FONT_SIZE.sm,
          fontWeight: 400,
          fontFamily: 'Public, sans-serif',
          cssClass: 'text-xs sm:text-sm  font-normal',
        },
        offsetY: -2,
      },
    },
    grid: {
      strokeDashArray: 5,
      padding: {
        left: 2,
        right: 2,
      },
      borderColor: 'rgba(107, 114, 128, .25)',
    },
    responsive: [
      {
        breakpoint: numberFromCSSUnit(theme.screens.sm as string),
        options: {
          ...PLOT_OPTIONS_RESPONSIVE[chartType],
          legend: {
            fontSize: FONT_SIZE.xs,
          },
          dataLabels: {
            style: {
              fontSize: FONT_SIZE.xs,
            },
          },
          xaxis: {
            labels: {
              style: {
                colors: theme.colors['text-support'],
                fontSize: FONT_SIZE.xs,
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                colors: theme.colors['text-support'],
                fontSize: FONT_SIZE.xs,
              },
              maxWidth: 150,
            },
          },
        },
      },
    ],
  };
  const chartOptionsByType = deepMerge(COMMON_CHART_OPTIONS, PLOT_OPTIONS[chartType]);

  return deepMerge(chartOptionsByType, options);
};

export const PIE_CHART_OTHER_COLOR = '#FDF6B2';
export const PIE_CHART_COLORS = [
  '#5480CD',
  '#3368C4',
  '#003CA5',
  '#002F81',
  '#8AA8DD',
  '#03543F',
  '#046C4E',
  '#0E9F6E',
  '#84E1BC',
  '#E6ECF8',
];
export const PIE_CHART_COLORS_BY_SEVERITY = ['#5480CD', '#E3A008', '#F98080', '#E02424'];
